import React, { useState, useEffect } from "react";
import { Header } from "../../Header";
import { local_signage, local_url } from "../../Const";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const formatDateForApi = (date) => {
  if (!date) return "";
  return date.format('YYYYMMDD');
};

const formatDisplayDateTime = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (isNaN(date.getTime()) || dateString.includes('1753-01-01') || dateString.includes('1723-01-01')) {
    return "-";
  }
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

export const HorecaRateHistory = () => {
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rateData, setRateData] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [rebateSchemeType, setRebateSchemeType] = useState('AREA');
  const [areas, setAreas] = useState([]);
  const [selectedArea, setSelectedArea] = useState('ALL');
  const [loadingAreas, setLoadingAreas] = useState(false);

  const token = localStorage.getItem('AuthorizationToken');
  const userId = localStorage.getItem('userId') || 'UnknownUser';

  const handleAuthError = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/login";
  };

  useEffect(() => {
    if (!token) return;
    const fetchAreas = async () => {
      setLoadingAreas(true);
      try {
        const response = await fetch(`${local_url}/api/SFArea/${userId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) throw new Error('Failed to fetch areas');
        const data = await response.json();
        setAreas(data.map((area) => ({ AreaId: area.sfAreaId, AreaName: area.sfAreaName })));
      } catch (err) {
        setError(err.message || 'Failed to fetch areas.');
      } finally {
        setLoadingAreas(false);
      }
    };
    fetchAreas();
  }, [token]);

  const handleSearch = async () => {
    if (!fromDate || !toDate) {
      setError('Please select both From Date and To Date.');
      return;
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      // Order: SFAreaID, RebateSchemeType, FromDate, ToDate
      const params = new URLSearchParams();
      params.set('SFAreaID', selectedArea);
      params.set('RebateSchemeType', rebateSchemeType);
      params.set('FromDate', formatDateForApi(fromDate));
      params.set('ToDate', formatDateForApi(toDate));

      const response = await fetch(
        `${local_signage}/api/v1/reportjsonrebateschemeratehistory?${params.toString()}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.status === 401) {
        handleAuthError();
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch rate history (${response.status})`);
      }

      const data = await response.json();
      setRateData(Array.isArray(data?.RebateRate) ? data.RebateRate : []);
    } catch (err) {
      setError(err.message || 'Failed to fetch rate history.');
      setRateData([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter matches Rebate Scheme ID, Rebate Scheme Name, or any linked Customer Name.
  const matchesSearch = (scheme) => {
    const term = searchText.trim().toLowerCase();
    if (!term) return true;

    const schemeFields = [scheme.RebateSchemeId, scheme.RebateSchemeName]
      .map((v) => String(v ?? '').toLowerCase());

    if (schemeFields.some((v) => v.includes(term))) return true;

    const customerNames = (scheme.cust || [])
      .map((c) => String(c.CustomerName ?? '').toLowerCase());

    return customerNames.some((v) => v.includes(term));
  };

  const filteredRateData = rateData.filter(matchesSearch);

  // Regular text/number fields - quoted only if they contain special chars.
  const escapeCsvValue = (value) => {
    const stringValue = value === null || value === undefined ? '' : String(value);
    return /[",\n]/.test(stringValue) ? `"${stringValue.replace(/"/g, '""')}"` : stringValue;
  };

  // ID fields (Rebate Scheme ID, Area ID, Unique ID, Item Code) can have leading zeros
  // (e.g. "0000000056"). A plain CSV cell like 0000000056 gets auto-converted to the
  // number 56 the moment Excel opens the file. Wrapping it as ="0000000056" forces
  // Excel to treat it as text and keep the leading zeros intact.
  const escapeCsvIdValue = (value) => {
    const stringValue = value === null || value === undefined ? '' : String(value);
    return `="${stringValue.replace(/"/g, '""')}"`;
  };

  const handleExportCsv = () => {
    const headers = [
      'Rebate Scheme ID', 'Rebate Scheme Name', 'Rate Scheme Type', 'Customer ID', 'Area Name', 'Area ID',
      'Date of Entry', 'Unique ID', 'Item Code', 'Item Name', 'Qualify Qty',
      'Rate', 'UOM', 'Valid Till'
    ];

    const csvRows = [];
    filteredRateData.forEach((scheme) => {
      (scheme.Revisions || []).forEach((revision) => {
        const items = revision.lineitems || [];
        const baseFields = [
          escapeCsvIdValue(scheme.RebateSchemeId),
          escapeCsvValue(scheme.RebateSchemeName),
          escapeCsvValue(scheme.RateSchemeType),
          escapeCsvIdValue(scheme.CustomerId),
          escapeCsvValue(scheme.AreaName),
          escapeCsvIdValue(scheme.AreaId),
          escapeCsvValue(formatDisplayDateTime(revision.DateOfEntry)),
          escapeCsvIdValue(revision.UniqueId)
        ];

        if (items.length === 0) {
          csvRows.push([...baseFields, '', '', '', '', '', ''].join(','));
          return;
        }

        items.forEach((item) => {
          csvRows.push([
            ...baseFields,
            escapeCsvIdValue(item.ItemCode),
            escapeCsvValue(item.ItemName),
            escapeCsvValue(item.QualifyQty),
            escapeCsvValue(item.Rate),
            escapeCsvValue(item.UOM),
            escapeCsvValue(formatDisplayDateTime(item.ValidTill))
          ].join(','));
        });
      });
    });

    const csvContent = ['sep=,', headers.join(','), ...csvRows].join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `horeca_rate_history_${formatDateForApi(fromDate)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Header />
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>HoReCa Rate History</Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="area-select-label" shrink>Area</InputLabel>
            <Select
              labelId="area-select-label"
              label="Area"
              value={selectedArea}
              onChange={(e) => setSelectedArea(e.target.value)}
            >
              <MenuItem value="ALL">
                {loadingAreas ? 'Loading areas...' : 'ALL'}
              </MenuItem>
              {areas.map((area) => (
                <MenuItem key={area.AreaId} value={area.AreaId}>
                  {area.AreaName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="rebate-scheme-type-label">Scheme Type</InputLabel>
            <Select
              labelId="rebate-scheme-type-label"
              label="Scheme Type"
              value={rebateSchemeType}
              onChange={(e) => setRebateSchemeType(e.target.value)}
            >
              <MenuItem value="AREA">AREA</MenuItem>
              <MenuItem value="CUST">CUST</MenuItem>
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="From Date"
              value={fromDate}
              onChange={(newValue) => setFromDate(newValue)}
              format="YYYYMMDD"
              slotProps={{ textField: { size: 'small' } }}
            />
            <DatePicker
              label="To Date"
              value={toDate}
              onChange={(newValue) => setToDate(newValue)}
              format="YYYYMMDD"
              slotProps={{ textField: { size: 'small' } }}
            />
          </LocalizationProvider>

          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Load Report'}
          </Button>

          <TextField
            size="small"
            placeholder="Search by scheme ID/scheme name/customer name"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ minWidth: 320, flexGrow: 1, maxWidth: 420 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />

          <Button
            variant="contained"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportCsv}
            disabled={filteredRateData.length === 0}
            sx={{ bgcolor: '#004d00', '&:hover': { bgcolor: '#003300' } }}
          >
            Export CSV
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {!loading && hasSearched && !error && rateData.length === 0 && (
          <Alert severity="info">No rate history found for the selected filters.</Alert>
        )}

        {!loading && hasSearched && !error && rateData.length > 0 && filteredRateData.length === 0 && (
          <Alert severity="info">No results match your search.</Alert>
        )}

        {filteredRateData.map((scheme) => (
          <Paper key={`${scheme.RebateSchemeId}-${scheme.AreaId}-${scheme.CustomerId || ''}`} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {scheme.RebateSchemeName || scheme.AreaName}
              </Typography>
              <Chip label={scheme.RateSchemeType || '-'} size="small" color="primary" variant="outlined" />
              <Chip label={`Scheme ID: ${scheme.RebateSchemeId}`} size="small" variant="outlined" />
              <Chip label={`Scheme Name: ${scheme.RebateSchemeName || '-'}`} size="small" variant="outlined" />
              {scheme.RebateSchemeName && scheme.AreaId && (
                <Chip label={`Area: ${scheme.AreaName} (${scheme.AreaId})`} size="small" variant="outlined" />
              )}
              {scheme.RateSchemeType === 'CUST' && scheme.CustomerId && (
                <Chip label={`Customer ID: ${scheme.CustomerId}`} size="small" variant="outlined" />
              )}
            </Box>

            {scheme.RateSchemeType === 'CUST' && (scheme.cust || []).length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary">Customers:</Typography>
                {scheme.cust.map((c, cIdx) => (
                  <Chip
                    key={`${c.CustomerId}-${cIdx}`}
                    label={`${c.CustomerName} (${c.CustomerId})`}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            )}

            {(!scheme.Revisions || scheme.Revisions.length === 0) && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No rate revisions found in the selected date range.
              </Typography>
            )}

            {(scheme.Revisions || []).map((revision, idx) => (
              <Accordion key={revision.UniqueId || idx} defaultExpanded={idx === 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatDisplayDateTime(revision.DateOfEntry)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Unique ID: {revision.UniqueId}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {(revision.lineitems || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', px: 1 }}>
                      No line items found for this revision.
                    </Typography>
                  ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Item Code</TableCell>
                          <TableCell>Item Name</TableCell>
                          <TableCell align="right">Qualify Qty</TableCell>
                          <TableCell align="right">Rate</TableCell>
                          <TableCell>UOM</TableCell>
                          <TableCell>Valid Till</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(revision.lineitems || []).map((item, itemIdx) => (
                          <TableRow key={`${item.ItemCode}-${itemIdx}`}>
                            <TableCell>{item.ItemCode}</TableCell>
                            <TableCell>{item.ItemName}</TableCell>
                            <TableCell align="right">{item.QualifyQty}</TableCell>
                            <TableCell align="right">{item.Rate}</TableCell>
                            <TableCell>{item.UOM}</TableCell>
                            <TableCell>{formatDisplayDateTime(item.ValidTill)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Paper>
        ))}
      </Box>
    </>
  );
};
